import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { NgFor, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { TranslocoModule } from '@ngneat/transloco';
import { catchError, debounceTime, filter, map, of, switchMap, tap } from 'rxjs';
import { resolveSearchResultLink } from './search-result-route.util';
import { SearchService } from './search.service';
import {
    SearchResponse,
    SearchResult,
    SearchResultGroup,
    SearchResultType,
    SEARCH_RESULT_TYPE_ICONS,
    SEARCH_RESULT_TYPE_LABEL_KEYS,
} from './search.types';

/** Group display order - most-used features first. */
const RESULT_TYPE_ORDER: SearchResultType[] = ['agent', 'data_source', 'table', 'hyperform', 'dashboard', 'report', 'tracker', 'task'];

@Component({
    selector       : 'search',
    templateUrl    : './search.component.html',
    styleUrls      : ['./search.component.scss'],
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'fuseSearch',
    animations     : fuseAnimations,
    standalone     : true,
    imports        : [
        NgIf, NgFor, FormsModule, ReactiveFormsModule,
        MatButtonModule, MatIconModule, MatAutocompleteModule, MatOptionModule,
        MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, MatTooltipModule,
        TranslocoModule,
    ],
    providers      : [
        {
            provide   : MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: (): (() => ScrollStrategy) =>
            {
                const overlay = inject(Overlay);
                return (): ScrollStrategy => overlay.scrollStrategies.block();
            },
        },
    ],
})
export class SearchComponent implements OnChanges, OnInit
{
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    @Output() search: EventEmitter<SearchResponse> = new EventEmitter<SearchResponse>();

    opened: boolean = false;
    loading: boolean = false;
    resultGroups: SearchResultGroup[] | null = null;
    readonly searchControl: UntypedFormControl = new UntypedFormControl();

    private _matAutocomplete: MatAutocomplete;
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _searchService = inject(SearchService);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): Record<string, boolean>
    {
        return {
            'search-appearance-bar'  : this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened'          : this.opened,
        };
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef)
    {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if ( value )
        {
            // Give Angular time to complete the change detection cycle
            setTimeout(() =>
            {
                // Focus to the input element
                value.nativeElement.focus();
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete)
    {
        this._matAutocomplete = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void
    {
        // Appearance
        if ( 'appearance' in changes )
        {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to the search field value changes
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                map((value: string | null) => (value ?? '').trim()),
                tap((value) =>
                {
                    // Below minLength (including empty): nothing in flight, clear the panel.
                    if ( value.length < this.minLength )
                    {
                        this.loading      = false;
                        this.resultGroups = null;
                        this._changeDetectorRef.markForCheck();
                    }
                }),
                filter(value => value.length >= this.minLength),
                tap(() =>
                {
                    this.loading = true;
                    this._changeDetectorRef.markForCheck();
                }),
                switchMap(value => this._searchService.search(value).pipe(
                    // A failed search should empty the panel, not leave it stuck loading.
                    catchError(() => of<SearchResponse>({ query: value, count: 0, results: [] })),
                )),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe((response) =>
            {
                this.loading      = false;
                this.resultGroups = this._groupResults(response.results);
                this.search.next(response);
                this._changeDetectorRef.markForCheck();
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void
    {
        // Escape
        if ( event.code === 'Escape' )
        {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if ( this.appearance === 'bar' && !this._matAutocomplete.isOpen )
            {
                this.close();
            }
        }
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void
    {
        // Return if it's already opened
        if ( this.opened )
        {
            return;
        }

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void
    {
        // Return if it's already closed
        if ( !this.opened )
        {
            return;
        }

        // Clear the search input and results
        this.searchControl.setValue('');
        this.resultGroups = null;
        this.loading      = false;

        // Close the search
        this.opened = false;
    }

    /**
     * Clear the query and results, keeping focus in the field so the next search
     * can be typed straight away.
     */
    clear(): void
    {
        this.searchControl.setValue('');
        this.resultGroups = null;
        this.loading      = false;
    }

    /**
     * Navigate to a result's click-through route (see `search-result-route.util.ts` for the
     * per-type mapping - some types only have a list route today) and reset the search bar.
     *
     * @param result
     */
    navigateToResult(result: SearchResult): void
    {
        this._router.navigate(resolveSearchResultLink(result));

        // Reset unconditionally (not via close(), which no-ops when `opened` is already false in
        // 'basic' appearance) so a stale result panel never lingers after navigating away.
        this.searchControl.setValue('');
        this.resultGroups = null;
        this.loading      = false;
        this.opened       = false;
    }

    /**
     * Track by function for the result-group ngFor
     *
     * @param index
     * @param group
     */
    trackByGroup(index: number, group: SearchResultGroup): SearchResultType
    {
        return group.type;
    }

    /**
     * Track by function for the result ngFor
     *
     * @param index
     * @param result
     */
    trackByResult(index: number, result: SearchResult): string
    {
        return `${result.type}-${result.id}`;
    }

    /**
     * The full path shown under a result's name. It is deliberately NOT trimmed against the
     * result name or the group heading: the whole trail is wanted on every row, even where it
     * repeats them.
     *
     * @param result
     */
    trailText(result: SearchResult): string
    {
        return (result.breadcrumb ?? [])
            .map(crumb => crumb.label)
            .filter(Boolean)
            .join(' › ');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Groups results by type, in a fixed feature order, preserving the backend's relative
     * (exact -> prefix -> substring) ranking within each group.
     *
     * @param results
     */
    private _groupResults(results: SearchResult[]): SearchResultGroup[]
    {
        const byType = new Map<SearchResultType, SearchResult[]>();

        for ( const result of results )
        {
            const bucket = byType.get(result.type) ?? [];
            bucket.push(result);
            byType.set(result.type, bucket);
        }

        return RESULT_TYPE_ORDER
            .filter(type => byType.has(type))
            .map(type => ({
                type,
                labelKey: SEARCH_RESULT_TYPE_LABEL_KEYS[type],
                icon    : SEARCH_RESULT_TYPE_ICONS[type],
                results : byType.get(type) as SearchResult[],
            }));
    }
}
